import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import CheckBox from 'components/final-form/CheckBox';
import FinalFormColorPicker from 'components/final-form/ColorPicker';
import { DropdownOption } from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import RadioGroup from 'components/final-form/RadioGroup';
import { generateRadioButtonOptions } from 'components/final-form/radioUtils';
import { selectInput } from 'components/final-form/SelectInput';
import TopRowEntityColorPicker from 'components/final-form/TopRowEntityColorPicker';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import PraxFormScrollableContainer from 'components/layout/PraxFormScrollableContainer';
import PxGrid from 'components/layout/PxGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import BillConfigurationsSection from 'components/mandate-settings/BillConfigurationsSection';
import BillInfoSection from 'components/mandate-settings/BillInfoSection';
import DignitySection from 'components/mandate-settings/DignitySection';
import MandateBankSection from 'components/mandate-settings/MandateBankSection';
import SectionsSection from 'components/mandate-settings/SectionsSection';
import arrayMutators from 'final-form-arrays';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Header, RadioProps } from 'semantic-ui-react';
import { addMandate, editMandate } from 'services/mandateConfigServices';
import styled from 'styled-components';
import { Module } from 'ts-types/api.enums';
import {
  MandateConfigDto,
  MandateQualitativeDto,
  UpsertMandateConfigDto,
  UpsertMandateTariffDto
} from 'ts-types/api.types';
import { required } from 'utils/validatorUtils';

const StyledForm = styled.form`
`;

const StyledField = styled(Field)`
  margin: 0 !important;
  padding: 0 !important;
`;

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
  toggleAddEditModal: () => void,
  onCancel: () => void,
  onSave: () => void,
  mandateId: number,
  mandate: MandateConfigDto
}

interface State {
  errorMessage?: string,
  modules: Array<DropdownOption>,
  mandateQualitatives: MandateQualitativeDto[],
  editedMandateTariffs: Array<UpsertMandateTariffDto>,
  cancelTokenSource: CancelTokenSource,
  editBill: boolean,
  editSection: boolean
}

class UpsertMandateForm extends Component<Props, State> {
  private praxis: Array<RadioProps>;

  constructor(props: Props) {
    super(props);

    const {getMessage, mandate} = this.props;
    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      modules: this.mapMandateModuleDropdownOptions(Object.values(Module)),
      mandateQualitatives: mandate ? mandate.mandateQualitatives : [],
      editedMandateTariffs: [],
      editSection: false,
      editBill: false,
      cancelTokenSource
    };

    const praxisLabels: Array<string> = ["doctor", "dentist", "physiotherapist", "lab"];
    const praxis = generateRadioButtonOptions(praxisLabels, "mandate", getMessage);
    this.praxis = praxis;
  };

  mapMandateModuleDropdownOptions = (list: Array<Module>): Array<DropdownOption> => {
    return list.map((element) => {
      return {
        key: element,
        text: this.props.getMessage(`enum.Module.${element}`),
        value: element
      }
    });
  };

  setMandateQualitatives = (mandateQualitatives: MandateQualitativeDto[]) => {
    this.setState({
      mandateQualitatives: mandateQualitatives
    })
  };

  handleSubmit = async (values: Partial<UpsertMandateConfigDto>) => {
    const {mandateId, getMessage, onSave} = this.props;
    const {cancelTokenSource, mandateQualitatives, editedMandateTariffs} = this.state;

    let saveValues: Partial<UpsertMandateConfigDto> = {...values};
    saveValues.mandateQualitatives = mandateQualitatives;
    saveValues.mandateTariffs = editedMandateTariffs;

    const handleError = () => {
      this.setState({
        errorMessage: getMessage("error.general")
      })
    };

    if (mandateId) {
      await editMandate(mandateId, saveValues, cancelTokenSource)
          .then(onSave)
          .catch(handleError)
    } else {
      await addMandate(saveValues, cancelTokenSource)
          .then(onSave)
          .catch(handleError)
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  render(): React.ReactNode {
    const {mandate} = this.props;

    return (
        <FinalForm
            onSubmit={this.handleSubmit}
            initialValues={mandate}
            subscription={{values: true, pristine: true, submitting: true}}
            render={this.renderForm}
            mutators={{
              ...arrayMutators
            }}
        />
    );
  }

  renderAddress = () => {
    const {getMessage, currentUser} = this.props;

    const userRoles = currentUser ? currentUser.roles : [];
    const showModifyPatientsCheckbox = userRoles.includes("ROLE_APP_ADMIN");

    return (
        <PraxFormGrid>
          <Grid.Row>
            <Grid.Column>
              <Header>{getMessage("mandate.address")}</Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage("mandate.name")}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  name="name"
                  component={Input}
                  validate={required}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage("mandate.street")}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  name="street"
                  component={Input}
                  validate={required}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage("mandate.zip")}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  name="zip"
                  component={Input}
                  validate={required}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage("mandate.city")}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  name="city"
                  component={Input}
                  validate={required}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage("mandate.speciality")}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  name="speciality"
                  component={Input}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage("mandate.tel")}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  name="tel"
                  component={Input}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage("mandate.fax")}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  name="fax"
                  component={Input}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage("mandate.email")}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  name="email"
                  component={Input}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage("mandate.emailArchive")}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  name="emailArchive"
                  component={Input}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage("mandate.mwstNumber")}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  name="mwstNumber"
                  component={Input}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage("mandate.registryKey")}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  name="registryKey"
                  component={Input}
                  validate={required}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <Header>{getMessage("mandate.options")}</Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage("mandate.eanNumber")}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  name="eanNumber"
                  component={Input}
                  validate={required}
                  autoFocus
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage("mandate.zsrNumber")}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  name="zsrNumber"
                  component={Input}
                  validate={required}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage("mandate.nif")}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  name="nif"
                  component={Input}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5}>
            </Grid.Column>
            <Grid.Column width={11}>
              <Field
                  name="praxis"
                  component={RadioGroup}
                  radioDefinitions={this.praxis}
                  radiowidth="25%"
                  validate={required}
              />
            </Grid.Column>
          </Grid.Row>
          {showModifyPatientsCheckbox &&
              <Grid.Row>
                <Grid.Column width={5}>
                </Grid.Column>
                <Grid.Column width={11}>
                  <Field
                      name="modifyPatientsInOtherMandates"
                      component={CheckBox}
                      label={getMessage("mandate.modifyPatientsInOtherMandates")}
                  />
                </Grid.Column>
              </Grid.Row>
          }

          {this.renderMediDataCredentialsSection()}
          {this.renderModulesSection()}
        </PraxFormGrid>
    );
  };

  renderMediDataCredentialsSection = (): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column>
              <Header>{getMessage("mandate.mediDataCredentials")}</Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage("mandate.mediDataClientId")}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  name="mediDataClientId"
                  component={Input}
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage("mandate.mediDataAuthToken")}
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  name="mediDataAuthToken"
                  component={Input}
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    )
  }

  renderModulesSection = (): React.ReactNode => {

    const {modules} = this.state;
    const {getMessage, currentUser} = this.props;

    const userRoles = currentUser ? currentUser.roles : [];
    const isAppAdmin = userRoles.includes("ROLE_APP_ADMIN");

    const validator = (value: string) => required(value);

    let moduleFieldProps = selectInput({
      name: "modules",
      options: modules,
      validate: isAppAdmin ? validator : undefined,
      multiple: true,
      disabled: !isAppAdmin,
      fluid: true
    }, {});

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column width={16} verticalAlign="middle">
              <Header>{getMessage("mandate.module")}</Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={16}>
              <StyledField
                  fluid
                  {...moduleFieldProps}
              />
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    )
  }

  renderForm = ({handleSubmit, submitting}: FormRenderProps): React.ReactNode => {

    const {getMessage, onCancel, mandate} = this.props;
    const {editBill, editSection, errorMessage} = this.state;

    return (
        <StyledForm onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              <PraxFormGrid>
                <PraxFormScrollableContainer height="900px">
                  {errorMessage
                      && (
                          <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                            {errorMessage}
                          </StyledErrorMessage>
                      )
                  }
                  <PxGrid>
                    <Grid.Row>
                      <Grid.Column width={16} textAlign="right">
                        <TopRowEntityColorPicker>
                          <Field
                              id="color"
                              name="color"
                              component={FinalFormColorPicker}
                          />
                        </TopRowEntityColorPicker>

                        <Field
                            name="active"
                            component={CheckBox}
                            label={`${getMessage("mandate.active")}`}
                            toggle={true}
                        />
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                      {this.renderAddress()}
                    </Grid.Row>
                    <Grid.Row>
                      {<BillInfoSection bills={mandate.bills} setEditBill={this.setEditBill}/>}
                    </Grid.Row>
                    <Grid.Row>
                      <DignitySection mandate={mandate} setMandateQualitatives={this.setMandateQualitatives}/>
                    </Grid.Row>
                    <Grid.Row>
                      <SectionsSection
                          sections={mandate.mandateSections ? mandate.mandateSections : []}
                          setEditSection={this.setEditSection}/>
                    </Grid.Row>
                    <Grid.Row>
                      <BillConfigurationsSection
                          mandateTariffs={mandate.mandateTariffs ? mandate.mandateTariffs : []}
                          addEditedMandateTariff={this.addEditedMandateTariff}
                          isMandateTariffDeleted={this.isMandateTariffDeleted}
                          getEditedMandateTariff={this.getEditedMandateTariff}/>
                    </Grid.Row>
                    <Grid.Row>
                      <MandateBankSection/>
                    </Grid.Row>
                  </PxGrid>
                </PraxFormScrollableContainer>
              </PraxFormGrid>
            </FormContent>

            <FormButtonsRow style={{marginTop: "1rem !important"}}>
              <Button compact type="submit" primary
                      disabled={submitting || editBill || editSection}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </StyledForm>
    );
  };

  setEditSection = (editSection: boolean) => {
    this.setState({
      editSection: editSection
    });
  };

  setEditBill = (editBill: boolean) => {
    this.setState({
      editBill: editBill
    });
  };

  addEditedMandateTariff = (mandateTariff: UpsertMandateTariffDto) => {
    const {editedMandateTariffs} = this.state;

    let newEditedMandateTariffs;

    if (editedMandateTariffs.find(mt => mt.tariffId === mandateTariff.tariffId)) {
      newEditedMandateTariffs = editedMandateTariffs.filter(mt => mt.tariffId !== mandateTariff.tariffId);
    } else {
      newEditedMandateTariffs = editedMandateTariffs;
    }

    if (mandateTariff.id !== null || !mandateTariff.toBeDeleted) {
      newEditedMandateTariffs.push(mandateTariff);
    }

    this.setState({
      editedMandateTariffs: newEditedMandateTariffs
    })
  };

  getEditedMandateTariff = (tariffId: number) => {
    const {editedMandateTariffs} = this.state;

    return editedMandateTariffs.find(mt => {
      return mt.tariffId === tariffId
    });
  };

  isMandateTariffDeleted = (tariffId: number) => {
    const {editedMandateTariffs} = this.state;

    let mandateTariff = editedMandateTariffs.find(mt => mt.tariffId === tariffId);
    return mandateTariff ? mandateTariff.toBeDeleted : false;
  }

}

export default withMessages(withAuthContext(UpsertMandateForm));
